import {
  EPaymentProvider,
  EPlanIntervalUnit,
  IPlan,
} from '@/models/checkout.model';
import { ANALYTICS } from '@/constants/analytics/analytics';
import { stringsUtil } from '@/utils';
import { ROUTES } from '@/constants/app/routes';
import { environment } from '@/environment';

type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];
type TrackedRoutePath = Exclude<
  RoutePath,
  typeof ROUTES.INSIGHTS | typeof ROUTES.WEB_HISTORY_CALLBACK
>;

const hasPlanWithTrial = (plans: IPlan[]): boolean =>
  plans?.some((plan: IPlan) => plan?.trial_period_in_days > 0);

const DURATION_DISPLAY: Record<EPlanIntervalUnit, string> = {
  [EPlanIntervalUnit.YEAR]: 'Annual',
  [EPlanIntervalUnit.MONTH]: 'Monthly',
  [EPlanIntervalUnit.SEMI]: 'Semi Annual',
  [EPlanIntervalUnit.LIFETIME]: 'Lifetime',
};

const planIntervalText = (planInterval: EPlanIntervalUnit): string =>
  DURATION_DISPLAY[planInterval] ?? planInterval;

const ROUTE_TO_SCREEN_NAME = {
  [ROUTES.HOME]: ANALYTICS.SCREEN.RULES_PAGE,
  [ROUTES.ABOUT]: ANALYTICS.EVENTS.ABOUT_PAGE,
  [ROUTES.PURCHASE]: ANALYTICS.EVENTS.PURCHASE_PAGE,
  [ROUTES.SETTINGS]: ANALYTICS.EVENTS.SETTINGS_PAGE,
  [ROUTES.BLOCKED]: ANALYTICS.SCREEN.BLOCK_PAGE,
  [ROUTES.UNINSTALL]: ANALYTICS.EVENTS.UNINSTALL_EXTENSION,
  [ROUTES.CUSTOMIZE_BLOCK_PAGE]: ANALYTICS.EVENTS.CUSTOMIZE_BLOCK_PAGE,
} satisfies Record<TrackedRoutePath, string>;

const DEFAULT_SCREEN_NAME = ANALYTICS.SCREEN.BLOCK_SITE_PAGE;

const stripBaseUrl = (path: string): string => {
  const stripped = path.replace(environment.baseUrl, '');
  return stripped === '' ? '/' : stripped;
};

const getScreenName = (pathname: string): string =>
  (ROUTE_TO_SCREEN_NAME as Record<string, string>)[pathname] ??
  DEFAULT_SCREEN_NAME;

export const getCurrentScreenName = (): string => {
  const path = stripBaseUrl(window.location.pathname);
  return getScreenName(path);
};

export const createPlanPickerViewPayload = (
  plans: IPlan[],
  source: string,
  analyticsData?: Record<string, unknown>,
  type = ANALYTICS.TYPES.SHOWN
): Record<string, unknown> => ({
  [ANALYTICS.PROPERTIES.EVENT_TYPE]: type,
  [ANALYTICS.PROPERTIES.SCREEN]: getCurrentScreenName(),
  [ANALYTICS.PROPERTIES.SOURCE]: source,
  [ANALYTICS.PROPERTIES.PLAN_IDS]: plans.flatMap(plan =>
    plan.providers.map(provider => provider.planId)
  ),
  [ANALYTICS.PROPERTIES.PLAN_NAMES]: plans.map(p => p.plan_name),
  [ANALYTICS.PROPERTIES.DURATIONS]: plans.map(p =>
    planIntervalText(p.interval_unit)
  ),
  [ANALYTICS.PROPERTIES.PRICES]: plans.map(p => p.price),
  [ANALYTICS.PROPERTIES.CURRENCY]: plans[0].currency,
  [ANALYTICS.PROPERTIES.IS_TRIAL]: hasPlanWithTrial(plans),
  [ANALYTICS.PROPERTIES.IS_IMMEDIATE_PAY]: !hasPlanWithTrial(plans),
  [ANALYTICS.PROPERTIES.DISCOUNT_PERCENTAGE]: 0, // TODO:: COMPLETE
  ...analyticsData,
});

export const createSubscriptionButtonClickPayload = (
  plan: IPlan,
  source: string,
  analyticsData?: Record<string, unknown>
): Record<string, unknown> => ({
  [ANALYTICS.PROPERTIES.SOURCE]: source,
  [ANALYTICS.PROPERTIES.SCREEN]: getCurrentScreenName(),
  [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.CLICK,
  [ANALYTICS.PROPERTIES.PLAN_NAME]: plan.plan_name,
  [ANALYTICS.PROPERTIES.PLAN_ID]: plan.providers.map(
    provider => provider.planId
  ),
  [ANALYTICS.PROPERTIES.PRICE]: plan.price,
  [ANALYTICS.PROPERTIES.CURRENCY]: stringsUtil.symbolByCurrency(plan.currency),
  [ANALYTICS.PROPERTIES.IS_PREMIUM_HOOK]: false,
  ...analyticsData,
});

export const createCheckoutModalShownPayload = (
  plan: IPlan,
  source: string,
  analyticsData?: Record<string, unknown>
): Record<string, unknown> => ({
  [ANALYTICS.PROPERTIES.SCREEN]: getCurrentScreenName(),
  [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.SHOWN,
  [ANALYTICS.PROPERTIES.SOURCE]: source,
  [ANALYTICS.PROPERTIES.PLAN_NAME]: plan.plan_name,
  [ANALYTICS.PROPERTIES.DURATION]: plan.interval_unit,
  [ANALYTICS.PROPERTIES.PRICE]: plan.price,
  [ANALYTICS.PROPERTIES.CURRENCY]: stringsUtil.symbolByCurrency(plan.currency),
  [ANALYTICS.PROPERTIES.IS_TRIAL]: plan.trial_period_in_days > 0,
  [ANALYTICS.PROPERTIES.IS_IMMEDIATE_PAY]: plan.trial_period_in_days === 0,
  ...analyticsData,
});

export const createSubscriptionSuccessPayload = (
  plan: IPlan,
  provider: EPaymentProvider,
  source: string,
  subscriptionId: string,
  analyticsData?: Record<string, unknown>
): Record<string, unknown> => ({
  [ANALYTICS.PROPERTIES.SOURCE]: source,
  [ANALYTICS.PROPERTIES.SCREEN]: getCurrentScreenName(),
  [ANALYTICS.PROPERTIES.PLAN_ID]: plan.providers.find(
    p => p.provider === provider
  )?.planId,
  [ANALYTICS.PROPERTIES.PLAN_NAME]: plan.plan_name,
  [ANALYTICS.PROPERTIES.DURATION]: plan.interval_unit,
  [ANALYTICS.PROPERTIES.PRICE]: plan.price,
  [ANALYTICS.PROPERTIES.CURRENCY]: plan.currency,
  [ANALYTICS.PROPERTIES.START_TRIAL]: plan.trial_period_in_days > 0,
  [ANALYTICS.PROPERTIES.SUBSCRIPTION_ID]: subscriptionId,
  [ANALYTICS.PROPERTIES.PAYMENT_PROVIDER]:
    provider === EPaymentProvider.STRIPE
      ? ANALYTICS.VALUES.STRIPE
      : ANALYTICS.VALUES.PAYPAL,
  ...analyticsData,
});
